import { Component, inject, OnInit } from '@angular/core';
import { ContractingAuthorityService } from '../contracting-authority.service';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getAcHistoryDataTableStructure } from './utils/ac-history-data-table-object';
import { AcHistory } from '@/types';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { getAcHistoryFiltersFormObject } from './utils/ac-history-filters-form-object';
import { ContractingAuthorityRepository } from '@/store/contracting-authority-state/contracting-authority-state.repository';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { BehaviorSubject, combineLatest, map, startWith } from 'rxjs';
import { CommonModule } from '@angular/common';
import { format } from 'date-fns';

@Component({
  selector: 'sm-ac-history',
  imports: [PageHeaderComponent, DataTableBuilderComponent, FormBuilderComponent, ButtonModule, TranslateModule, CommonModule],
  templateUrl: './ac-history.component.html',
  styleUrl: './ac-history.component.scss',
})
export class AcHistoryComponent implements OnInit {
  contractingAuthorityService = inject(ContractingAuthorityService);
  acRepository = inject(ContractingAuthorityRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);

  breadcrumb = [
    { label: 'settings.contractingAuthority.breadcrumb.parametres', routerLink: ['/settings'] },
    {
      label: 'settings.contractingAuthority.breadcrumb.authorities',
      routerLink: ['/settings', 'contracting-authority'],
    },
    {
      label: 'settings.contractingAuthority.breadcrumb.authorities',
      routerLink: ['/settings', 'contracting-authority', 'ac-history'],
    },
  ];

  dataTableObject = getAcHistoryDataTableStructure();
  data: AcHistory[] = [];
  loading = false;

  acOptions: SelectOption[] = [];
  selectedDate = new BehaviorSubject<Date | null>(null);
  acId = this.acRepository.getObservable<SelectOption>('acSelected').pipe(map(ac => (ac ? Number(ac.code) : null)));

  isSearchDisabled$ = combineLatest([this.acId, this.selectedDate]).pipe(
    map(([acId, date]) => acId === null || date === null),
    startWith(true)
  );

  formObject: DynamicForm = getAcHistoryFiltersFormObject({
    store: this.acRepository,
    acOptions: this.acOptions,
    selectedDate: this.selectedDate,
  });

  ngOnInit() {
    this.contractingAuthorityService.getContractingAuthoritiesList().subscribe({
      next: response => {
        this.acOptions = response.map(ac => ({ name: ac.nom, code: ac.id }));
        this.formObject = getAcHistoryFiltersFormObject({
          store: this.acRepository,
          acOptions: this.acOptions,
          selectedDate: this.selectedDate,
        });
      },
    });
  }

  confirm() {
    const selectedDate = this.selectedDate.getValue();

    const acId = Number(this.acRepository.get<SelectOption>('acSelected')?.code);
    this.loading = true;
    if (acId && selectedDate) {
      this.contractingAuthorityService.getAcHistory(acId, format(selectedDate, 'yyyy-MM-dd')).subscribe({
        next: response => {
          this.data = response.content;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.contractingAuthority.history.message.error.loading'),
          });
        },
      });
    }
  }
}
